import { forwardRef, type InputHTMLAttributes } from 'react'
import { Check, Minus } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * Checkbox Component
 * Based on UX Design Guidelines §7.2 - Form Inputs
 *
 * Features:
 * - Checked, unchecked, indeterminate states
 * - Label and description support
 * - Accessible with proper ARIA attributes
 * - Entire component is a click target (outer <label> wraps everything)
 */

export interface CheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string
  description?: string
  error?: string
  /** Indeterminate state (partial selection) */
  indeterminate?: boolean
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      label,
      description,
      error,
      indeterminate = false,
      className,
      id,
      checked,
      disabled,
      ...props
    },
    ref
  ) => {
    const inputId = id || props.name
    const hasError = Boolean(error)

    return (
      <label
        htmlFor={inputId}
        className={cn(
          'relative flex items-start',
          disabled ? 'cursor-not-allowed' : 'cursor-pointer',
          className
        )}
      >
        {/* Hidden native input */}
        <div className="flex items-center h-5">
          <input
            ref={(node) => {
              if (node) {
                node.indeterminate = indeterminate
              }
              if (typeof ref === 'function') {
                ref(node)
              } else if (ref) {
                ref.current = node
              }
            }}
            id={inputId}
            type="checkbox"
            checked={checked}
            disabled={disabled}
            aria-invalid={hasError}
            aria-describedby={
              error
                ? `${inputId}-error`
                : description
                  ? `${inputId}-description`
                  : undefined
            }
            className="sr-only peer"
            {...props}
          />

          {/* Custom checkbox visual */}
          <div
            className={cn(
              'w-5 h-5 rounded flex items-center justify-center',
              'border-2 transition-colors duration-small',
              'peer-focus-visible:ring-2 peer-focus-visible:ring-accent/40 peer-focus-visible:ring-offset-2',
              // Checked state
              checked || indeterminate
                ? 'bg-accent border-accent text-white'
                : hasError
                  ? 'border-error bg-white'
                  : 'border-gray-300 bg-white',
              // Disabled state
              disabled && 'opacity-50 cursor-not-allowed'
            )}
            aria-hidden="true"
          >
            {indeterminate ? (
              <Minus className="w-3.5 h-3.5" strokeWidth={3} />
            ) : checked ? (
              <Check className="w-3.5 h-3.5" strokeWidth={3} />
            ) : null}
          </div>
        </div>

        {/* Label and description */}
        {(label || description) && (
          <div className="ml-3">
            {label && (
              <span
                className={cn(
                  'text-body font-medium',
                  disabled ? 'text-gray-400' : 'text-gray-700'
                )}
              >
                {label}
              </span>
            )}
            {description && (
              <p
                id={`${inputId}-description`}
                className="text-caption text-gray-500"
              >
                {description}
              </p>
            )}
          </div>
        )}

        {/* Error message */}
        {error && (
          <p
            id={`${inputId}-error`}
            className="absolute -bottom-5 left-8 text-caption text-error"
            role="alert"
          >
            {error}
          </p>
        )}
      </label>
    )
  }
)

Checkbox.displayName = 'Checkbox'
